import { z } from 'zod';
import { useCallback } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import { DateTime } from 'luxon';
import {
  PULSE_CACHE_KEYS,
  PULSE_QUERY_CACHE_POLICIES,
  useCachedQuery,
} from './cache';

export type IntegrationHealth = {
  integrationId: string;
  lastCollectionTime?: DateTime;
  factsCount: number;
  isHealthy: boolean;
  relatedChecksCount: number;
};

const integrationHealthResponseSchema = z.object({
  integrationId: z.string(),
  lastCollectionTime: z.string().nullable().optional(),
  factsCount: z.number(),
  isHealthy: z.boolean(),
  relatedChecksCount: z.number(),
});

const fetchIntegrationHealth = async (
  fetch: typeof globalThis.fetch,
  integrationId: string,
): Promise<IntegrationHealth> => {
  const response = await fetch(
    `plugin://pulse/integrations/${integrationId}/latest-collection`,
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch integration health: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  const parsed = integrationHealthResponseSchema.parse(data);

  return {
    integrationId: parsed.integrationId,
    lastCollectionTime: parsed.lastCollectionTime
      ? DateTime.fromISO(parsed.lastCollectionTime)
      : undefined,
    factsCount: parsed.factsCount,
    isHealthy: parsed.isHealthy,
    relatedChecksCount: parsed.relatedChecksCount,
  };
};

export const useIntegrationHealth = (integrationId: string) => {
  const fetchApi = useApi(fetchApiRef);
  const fetchHealth = useCallback(
    () => fetchIntegrationHealth(fetchApi.fetch.bind(fetchApi), integrationId),
    [fetchApi, integrationId],
  );

  return useCachedQuery({
    cacheKey: PULSE_CACHE_KEYS.integrationHealth(integrationId),
    fetcher: fetchHealth,
    ...PULSE_QUERY_CACHE_POLICIES.health,
    enabled: Boolean(integrationId),
  });
};
